import { parseE164Phone } from '@ps/functions/auth/formatPhoneNumber';

/** A guest phone split into the two parts every consumer needs together. */
export interface ResolvedGuestPhone {
  /** National digits only — what the masked phone input renders. */
  phone: string | null;
  /** ISO region those digits belong to. `null` only when there is no phone at all. */
  phoneCountry: string | null;
}

/**
 * Split a guest phone from ANY carrier into the canonical (national digits, ISO region)
 * pair — the one shape the phone input, the validity rules, and the lead API all expect.
 *
 * Two carriers exist and neither can be trusted to be pre-split:
 *  - an **international** number, dial code included: a bio lead-form / guest-contact gate
 *    submits `${dialCode}${national}`, and a hand-built or third-party `?guest_phone=`
 *    link can be full E.164. The region comes from the number itself; a `phoneCountry`
 *    argument would contradict it, so it's ignored.
 *  - **national digits + an explicit region**, which is what our own return / retry URLs
 *    write.
 *
 * Formatting is stripped either way, so `(202) 555-0123` lands as `2025550123` and the
 * mask renders it. An unparseable international number keeps its digits (the buyer can
 * see and fix what was carried) and falls back to the given region.
 */
export function resolveGuestPhone(
  phone: string | null | undefined,
  phoneCountry: string | null | undefined,
): ResolvedGuestPhone {
  const carried = phone?.trim();
  // No phone → no region to report: a country alone would describe nothing, and would
  // otherwise leak onto URLs for buyers who entered no number.
  if (!carried) {
    return { phone: null, phoneCountry: null };
  }
  if (carried.startsWith('+')) {
    const parsed = parseE164Phone(carried);
    if (parsed) {
      return {
        phone: parsed.nationalNumber,
        phoneCountry: parsed.countryCode.toUpperCase(),
      };
    }
  }
  const digits = carried.replace(/\D/g, '');
  return {
    phone: digits || null,
    phoneCountry: digits ? (phoneCountry?.toUpperCase() ?? null) : null,
  };
}
